import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useCompanyStore } from "../stores/companyStore";
import { CompanyForm } from "./CompanyForm";
import { ManagerList } from "./ManagerList";
import { CompanyAdditionalInfoPanel } from "./CompanyAdditionalInfoPanel";
import { CompanyCarSpecPanel } from "./CompanyCarSpecPanel";
import { GuestSmsTemplatePanel } from "./GuestSmsTemplatePanel";
import { RecvocTenantPanel } from "@/features/recvocTenant";
import { CallPassSettingPanel } from "@/features/callpass";
import { CompanyApiKeyPanel } from "@/features/companyApiKey";

/**
 * 업체 상세는 설정 항목이 계속 늘어나는 화면이라 가로 탭이 먼저 무너진다.
 * 성격별로 묶은 세로 레일로 두면 항목이 늘어도 그룹 안에 붙기만 하면 된다.
 *
 * - 기본: 업체 자체를 식별하는 정보
 * - 운영 설정: 그 업체 화면·문서 동작을 바꾸는 값
 * - 연동 · 키: 외부 시스템과 붙는 인증정보 (유출 주의 대상)
 */
const TAB_GROUPS = [
  {
    label: "기본",
    tabs: [
      { value: "info", label: "업체 정보" },
      { value: "managers", label: "담당자" },
    ],
  },
  {
    label: "운영 설정",
    tabs: [
      { value: "additionalInfo", label: "프로덕트 설정" },
      { value: "carSpec", label: "전용 톤수" },
      { value: "guestSms", label: "게스트 SMS" },
    ],
  },
  {
    label: "연동 · 키",
    tabs: [
      { value: "callpass", label: "정보망 연동" },
      { value: "apiKeys", label: "외부 API 키" },
      { value: "recvoc", label: "RecVoc 연결" },
    ],
  },
] as const;

type TabValue = (typeof TAB_GROUPS)[number]["tabs"][number]["value"];

export function CompanyDrawer() {
  const drawerOpen = useCompanyStore((s) => s.drawerOpen);
  const selectedGuid = useCompanyStore((s) => s.selectedGuid);
  const closeDrawer = useCompanyStore((s) => s.closeDrawer);
  const [activeTab, setActiveTab] = useState<TabValue>("info");

  const isEditMode = !!selectedGuid;

  const handleClose = () => {
    closeDrawer();
    setActiveTab("info");
  };

  return (
    <Sheet
      open={drawerOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex flex-col overflow-hidden sm:max-w-[880px]"
      >
        <SheetHeader>
          <SheetTitle>
            {isEditMode ? "배차 업체 수정" : "배차 업체 등록"}
          </SheetTitle>
          <SheetDescription>
            {isEditMode
              ? "배차 업체 정보를 수정합니다"
              : "기존 업체를 검색하여 배차 권한을 부여합니다"}
          </SheetDescription>
        </SheetHeader>

        {isEditMode ? (
          <div className="flex flex-1 overflow-hidden border-t border-gray-200">
            <nav
              role="tablist"
              aria-orientation="vertical"
              className="w-40 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50/60 p-2"
            >
              {TAB_GROUPS.map((group) => (
                <div key={group.label} className="mb-2 last:mb-0">
                  <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {group.label}
                  </p>
                  {group.tabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={cn(
                        "mb-0.5 w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                        activeTab === tab.value
                          ? "bg-white font-medium text-gray-900 shadow-sm"
                          : "text-gray-600 hover:bg-gray-100",
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            <div className="flex flex-1 flex-col overflow-hidden">
              {activeTab === "info" && (
                <CompanyForm
                  key={selectedGuid}
                  mode="edit"
                  companyGuid={selectedGuid}
                />
              )}
              {activeTab === "managers" && (
                <ManagerList companyGuid={selectedGuid} />
              )}
              {activeTab === "additionalInfo" && (
                <CompanyAdditionalInfoPanel
                  key={selectedGuid}
                  companyGuid={selectedGuid}
                />
              )}
              {activeTab === "carSpec" && (
                <CompanyCarSpecPanel
                  key={selectedGuid}
                  companyGuid={selectedGuid}
                />
              )}
              {activeTab === "guestSms" && (
                <GuestSmsTemplatePanel
                  key={selectedGuid}
                  companyGuid={selectedGuid}
                />
              )}
              {activeTab === "callpass" && (
                <CallPassSettingPanel companyGuid={selectedGuid} />
              )}
              {activeTab === "apiKeys" && (
                <CompanyApiKeyPanel
                  key={selectedGuid}
                  companyGuid={selectedGuid}
                />
              )}
              {activeTab === "recvoc" && (
                <RecvocTenantPanel companyGuid={selectedGuid} />
              )}
            </div>
          </div>
        ) : (
          <CompanyForm key="add" mode="add" companyGuid={null} />
        )}
      </SheetContent>
    </Sheet>
  );
}
